import React from "react";
import { Helmet } from "react-helmet-async";
import { SITE_NAME, DEFAULT_DESC, DEFAULT_IMAGE, canonicalUrl, truncate } from "@/lib/seo";

/**
 * Reusable SEO head for every page.
 * Props:
 *  - title: page title (site name is appended unless titleFull)
 *  - description: meta description
 *  - path: route path used to build the canonical URL (defaults to current location)
 *  - image: absolute OG/Twitter image URL
 *  - type: OG type (website | article | profile)
 *  - noindex: exclude from search engines
 *  - jsonLd: a single object or array of JSON-LD structured-data objects
 *  - titleFull: use title verbatim (don't append the site name)
 */
export function Seo({ title, description, path, image, type = "website", noindex = false, jsonLd, titleFull = false }) {
  const canonical = canonicalUrl(path || (typeof window !== "undefined" ? window.location.pathname : "/"));
  const fullTitle = title ? (titleFull ? title : `${title} · ${SITE_NAME}`) : `${SITE_NAME} — Explore America`;
  const desc = truncate(description || DEFAULT_DESC, 160);
  const img = image || DEFAULT_IMAGE;
  const blocks = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]).filter(Boolean) : [];

  return (
    <Helmet prioritizeSeoTags>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      <link rel="canonical" href={canonical} />
      {noindex ? <meta name="robots" content="noindex,follow" /> : <meta name="robots" content="index,follow" />}

      {/* Open Graph */}
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={img} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={img} />

      {blocks.map((b, i) => (
        <script key={i} type="application/ld+json">{JSON.stringify(b)}</script>
      ))}
    </Helmet>
  );
}

export default Seo;
